import { Box } from '@wix/design-system';
import { SectionId } from '@wix/wix-help-widget-common/homepage';
import React, {
  FC,
  UIEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, To, useSearchParams } from 'react-router-dom';

import { HelpCenterChatStatusBanner } from '../../chatbot/components/Chat/HelpCenterChat/HelpCenterChatStatusBanner';
import { Loader } from '../../common/components/Loader';
import { useAnimation } from '../../common/hooks/useAnimation';
import { usePageEngagement } from '../../common/hooks/usePageEngagement';
import { homepageSlice } from '../../common/reducers/homepage';
import {
  selectHomepageContent,
  selectRecommendedArticles,
  selectRecommendedArticlesSection,
  selectScrolledToTop,
} from '../../common/selectors/homepage';
import { homepageService } from '../../common/services/homepage/homepageService';
import { appRoutes } from '../../react-router';
import { classNames } from '../../util';
import { biHelpCenter } from '../../util/bi';
import { emitter } from '../../util/emitter';
import { Section } from './Section';
import s from './styles.scss';

export const Home: FC = () => {
  const dispatch = useDispatch();
  const homepageScrolledToTop = useSelector(selectScrolledToTop);
  const [searchParams] = useSearchParams();
  const { onScroll: onPageEngagementScroll } = usePageEngagement();
  const { data: recommendedArticlesData } = useSelector(
    selectRecommendedArticles,
  );
  const { data, loading, error } = useSelector(selectHomepageContent);

  // If this component is visible then the widget is expanded already
  const [isWidgetExpanded, setIsWidgetExpanded] = useState(true);

  const recommendedArticlesSection = useSelector(
    selectRecommendedArticlesSection,
  );
  const activeAnimation = useAnimation(s.moveInRight, s.moveInLeft, []);
  const homepageElementRef = useRef<HTMLDivElement>(null);

  const onScroll = useCallback(
    (e: UIEvent<HTMLDivElement>) => {
      onPageEngagementScroll(e);
      const { scrollTop } = e.target as HTMLDivElement;
      const scrolledToTop = scrollTop === 0;

      if (scrolledToTop !== homepageScrolledToTop) {
        dispatch(homepageSlice.actions.setScrolledToTop(scrolledToTop));
      }
    },
    [onPageEngagementScroll, homepageScrolledToTop, dispatch],
  );

  useEffect(() => {
    const waitForArticles = () =>
      recommendedArticlesSection && !recommendedArticlesData;

    if (
      !homepageService.homepageEnabled ||
      !data ||
      waitForArticles() ||
      !isWidgetExpanded
    ) {
      return;
    }

    if (homepageElementRef.current) {
      dispatch(
        homepageSlice.actions.setScrolledToTop(
          homepageElementRef.current.scrollTop === 0,
        ),
      );
    }

    // Fix race condition with state monitor
    Promise.resolve().then(() => {
      biHelpCenter.articleRecommendationVersion =
        recommendedArticlesData?.modelId;
      biHelpCenter.logHomePagePresented(
        data,
        recommendedArticlesData?.articles || null,
      );
    });
  }, [data, recommendedArticlesData]);

  useEffect(() => {
    const callback = (widgetExpanded) => {
      setIsWidgetExpanded(widgetExpanded);

      if (widgetExpanded && data) {
        biHelpCenter.logHomePagePresented(
          data,
          recommendedArticlesData?.articles || null,
        );
      }
    };

    emitter.on('widgetExpanded', callback);
    return () => emitter.removeListener('widgetExpanded', callback);
  }, [data, recommendedArticlesData]);

  if (!homepageService.homepageEnabled) {
    const redirectTo: To = {
      pathname: appRoutes.categories(),
      search: searchParams.toString(),
    };
    return <Navigate to={redirectTo} replace />;
  }

  // TODO:homepage handle error
  if (loading || !data || error) {
    return <Loader height="100%" width="100%" verticalAlign="middle" />;
  }

  return (
    <div
      className={classNames([s.home, activeAnimation])}
      data-hook="homepage"
      onScroll={onScroll}
      ref={homepageElementRef}
    >
      <HelpCenterChatStatusBanner />
      <Box direction="vertical" padding="0 18px">
        {data.sections.map((section, index) => (
          <Section key={section.sectionId} section={section} index={index} />
        ))}
      </Box>
    </div>
  );
};
